import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IOutputProgressStatus } from '@blcloud/bl-datamodel';
import {
  AutoUpdateIntervalUnit,
  FnKey,
  FunctionRef,
  FunctionRole,
  PageId,
  PreviewType,
  PrintPreviewModalComponent,
  ScreenService,
} from '@blcloud/bl-ng-share-module';
import { fromEvent as ObservableFromEvent } from 'rxjs/observable/fromEvent';
import { empty as ObservableEmpty } from 'rxjs/observable/empty';
import {
  AbstractContainer,
  BlDateComponent,
  BlDialogService,
  BlLoadingService,
  BlModalService,
  BlReferenceGridComponent,
  Era,
  LoadingOption,
  ModalButtonType,
  ModalReason,
} from '@blcloud/bl-ng-ui-component';
import { ReportOutputStatusPageService } from './report-output-status-page.service';
import { GridOptions } from 'ag-grid';
import { IReportOutputStatusSearchInfo } from './report-output-status-define';
import { OutputProgressStatusDiv, OutputProgressStatusDivArray } from '@blcloud/bl-datamodel/enum/bizcmn/output-progress-status-div';
import { DateSeparator, DateTimeOutput, DateTimePadding, DateTimeUtils } from '@blcloud/bl-common';
import { BlListResponseBody } from '@blcloud/bl-ng-common';
import { debounceTime, mergeMap, takeUntil, tap, delay } from 'rxjs/operators';
import {
  cloneDeep as _cloneDeep, isNil as _isNil, defer as _defer
} from 'lodash';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs';

/**
 * 帳票出力状況画面エントリ コンテナコンポーネント
 */
@Component({
  selector: 'app-report-output-status-page',
  templateUrl: './report-output-status-page.component.html',
  styleUrls: ['./report-output-status-page.component.scss'],
})
export class ReportOutputStatusPageComponent extends AbstractContainer implements OnInit, OnDestroy {

  readonly Era = Era;

  readonly ModalButtonType = ModalButtonType;

  readonly AutoUpdateIntervalUnit = AutoUpdateIntervalUnit;

  readonly LOCAL_STORAGE_AUTO_UPDTAE = 'share_report_output_status_guide_auto_update';
  /**
   * 抽出日付Start
   */
  @ViewChild('searchDateStart') searchDateStart: BlDateComponent;
  /**
   * 抽出日付End
   */
  @ViewChild('searchDateEnd') searchDateEnd: BlDateComponent;
  /**
   * グリッド
   */
  @ViewChild('refGrid') refGrid: BlReferenceGridComponent;

  /**
   * 検索用情報
   */
  searchInfo: IReportOutputStatusSearchInfo = {
    pageIds: []
  };

  /**
   * 機能名配列
   */
  reportNameDivArray = new Array();
  /**
   * 出力状況配列
   */
  statusDivEnumArray = new Array();

  /**
   * 表示する行数の選択肢
   */
  pageSizePatterns: number[] = [50, 100, 500, 1000];

  /**
   * 総件数
   */
  totalCount = 0;

  /**
   * ページサイズ
   */
  pageSize = 50;

  /**
   * grid設定値
   */
  gridOptions: GridOptions;

  /** 最終更新日時 */
  latestUpdateDateTime = null;
  /** 自動更新用ストレージアクセスキー */
  accessKey = this.LOCAL_STORAGE_AUTO_UPDTAE;
  /** 検索時のSubject */
  private searchSubject = new Subject<{page: number, size: number, reload: boolean}>();
  /** 初期化時のページ番号 */
  private initializePage = -1;
  /** 現在のページ番号 */
  private currentPage = -1;
  /** 現在の表示件数 */
  private currentPerPage = -1;
  /** 購読管理 */
  private onDestroy$ = new Subject<void>();
  /** 標準の更新間隔(20s) */
  private defaultInterval = 20;
  interval = this.defaultInterval;
  /** 購読管理 */
  private subscription = new Subscription();

  /**
   * grid選択値
   */
  private selectedData: IOutputProgressStatus;

  /**
   * コンストラクタ
   * @param screenService 画面共通サービス
   * @param blLoadingService ローディングサービス
   * @param service 帳票出力状況サービス
   * @param dialogService メッセージサービス
   * @param modalService モーダルサービス
   */
  constructor(
    private screenService: ScreenService,
    private blLoadingService: BlLoadingService,
    private service: ReportOutputStatusPageService,
    private dialogService: BlDialogService,
    private modalService: BlModalService,
  ) {
    super();
  }

  /**
   * 初期化処理
   */
  ngOnInit(): void {
    this.screenService.setTitle('帳票出力状況');
    this.initScreen();

    this.subscription.add(this.service.initializeOrganization().subscribe(
      (res) => {
        this.searchInfo.blTenantId = res.blTenantId;
        this.searchInfo.blUserId = res.blUserId;
        this.searchInfo.organizationCode = res.organizationCode;
        this.searchInfo.employeeCode = res.employeeCode;
      }
    ));

    this.reportNameDivArray = this.service.addReportNameComboBox();
    this.statusDivEnumArray = this.service.addStatusDispAll(OutputProgressStatusDivArray);
    this.resetCondition();

    this.gridOptions = this.service.gridOptions;
    // ウィンドウリサイズ購読
    this.subscription.add(ObservableFromEvent(window, 'resize').pipe(
      takeUntil(this.onDestroy$),
      debounceTime(100),
    ).subscribe(
      () => this.adjustColumn()
    ));

    this.subscription.add(this.searchSubject.pipe(
      tap(() => {
        this.showLoading({focusKeep: true});
      }),
      delay(300), // changeEventの反映を待つ
      mergeMap((param: {page: number, size: number, reload: boolean}) => {
        const dateStart = this.convertDate(this.searchInfo.searchDateStart);
        const dateEnd = this.convertDate(this.searchInfo.searchDateEnd);
        if (!_isNil(this.searchDateStart._year) && !_isNil(this.searchDateEnd._year) && +dateEnd < +dateStart) {
          // 入力データエラーの場合
          this.hideLoading();
          return this.dialogService.error('抽出日付の範囲指定に誤りがあります。').pipe(
            mergeMap((ref) => {
              ref.hide();
              if (!_isNil(this.searchDateStart)) {
                this.searchDateStart.focus();
              }
              return ObservableEmpty();
            })
          );
        } else {
          this.searchInfo.pageIds = this.service.getPageIds(this.searchInfo.pageId);
          return this.service.getOutputStatus(this.searchInfo, param.page, param.size).pipe(
            tap((response: BlListResponseBody<IOutputProgressStatus>) => {
              if (0 < response.totalCount) {
                this.currentPage = param.page - 1;
              }
              if (param.reload) {
                this.latestUpdateDateTime = DateTimeUtils.formatIso(DateTimeUtils.now());
              }
            })
          );
        }
      })
    ).subscribe((response: BlListResponseBody<IOutputProgressStatus>) => {
      this.hideLoading();
      if (!_isNil(response)) {
        this.totalCount = response.totalCount;
        this.refGrid.setData(response.searchResultList);
        if (0 < this.totalCount) {
          this.adjustColumn();
          this.refGrid.focus();
        } else {
          // 0件なので日付にフォーカス
          this.searchDateStart.focus();
        }
      }
    }));
  }

  /**
   * @override
   */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * 画面情報を初期化します。
   */
  initScreen(): void {
    this.screenService.initializeScreen(PageId.ReportOutputStatus);
    this.setFunctions();
  }

  /**
   * 情報のFnキーを設定します
   * @param functions 情報のFnキー
   */
  private setFunctions(
    functions: (any)[] = [
      [
        FnKey.Search,
        FnKey.Clear
      ]
    ]
  ) {
    this.screenService.setFunctions(functions).subscribe(this.subscribeFunctions.bind(this));
    this.screenService.setLabelFnKey(FunctionRole.Next, '帳票表示');
  }

  /**
   * 共通のFnキー押下を処理します。
   * @param fnRef ファンクション
   */
  private commonFunctions(fnRef: FunctionRef): void {

    switch (fnRef.role) {
      // トップメニュー
      case FunctionRole.TopMenu:
        fnRef.end();
        break;
      default:
        fnRef.end();
        break;
    }
  }

  /**
   * 情報のFnキー押下を購読します。
   * @param fnRef ファンクション
   */
  private subscribeFunctions(fnRef: FunctionRef): void {

    switch (fnRef.role) {
      // 検索
      case FunctionRole.Search: this.doSearch(fnRef); break;
      // 条件クリア
      case FunctionRole.Clear: this.doClear(fnRef); break;
      // 帳票表示
      case FunctionRole.Next: this.doShowPdf(fnRef); break;
      // その他
      default: this.commonFunctions(fnRef); break;
    }
  }

  /**
   * 検索します。
   * @param fnRef ファンクション
   */
  private doSearch(fnRef: FunctionRef): void {
    this.search(1, this.pageSize);
    fnRef.end();
  }

  /**
   * 条件をクリアします。
   * @param fnRef ファンクション
   */
  private doClear(fnRef: FunctionRef): void {
    this.clear();
    fnRef.end();
  }

  /**
   * PDFを表示します。
   * @param fnRef ファンクション
   */
  private doShowPdf(fnRef: FunctionRef): void {
    this.showPdf();
    fnRef.end();
  }



  /**
   * 参照用グリッド初期処理完了通知
   */
  onReadyGrid() {
    this.refGrid.gridOptions.api.setColumnDefs(_cloneDeep(this.gridOptions.columnDefs));
    this.refGrid.showNoRows();
    this.adjustColumn();
  }

  /**
   * ページング: ページサイズ変更時処理
   * @param size 表示件数
   */
  onChangePageSize(size: number) {
    this.pageSize = size;
  }

  /**
   * 明細選択時のイベント
   *
   * @param info
   */
  onSelectedRowEvent(info: IOutputProgressStatus) {
    this.selectedData = info;
  }

  /**
   * リロード処理
   *
   */
  reload() {
    this.search(this.currentPage + 1, this.currentPerPage, true);
  }

  /**
   * 検索処理
   *
   * @param page
   * @param size
   * @param reload
   */
  private search(page: number, size: number, reload = false) {
    this.searchSubject.next({
      page, size, reload
    });
  }

  /**
   * 条件クリア処理
   */
  clear() {
    this.resetCondition();

    this.refGrid.clear();
    this.adjustColumn();
  }

  /**
   * 帳票プレビューを表示します
   */
  showPdf() {
    if (!_isNil(this.selectedData) &&
      this.selectedData.outputProgressStatusDiv === OutputProgressStatusDiv.Complete) {
      this.dialogService.confirm('帳票プレビューを表示します。\nよろしいですか？').subscribe((dialog) => {
        if (dialog.reason === ModalReason.Done) {
          this.modalService.show(PrintPreviewModalComponent, {
            data: {
              /** タイトル */
              title: this.selectedData.reportLayoutFileName,
              /** 画像取得にAPIを使用するか */
              isApiUse: false,
              /** 画像ID（API取得） */
              id: this.selectedData.pageId,
              /** 画像ソース（isApiUse=false） */
              src: this.selectedData.reportFileUrl,
              /** プレビュータイプ */
              previewType: PreviewType.Pdf,
            }
          }).subscribe((ref) => {
            ref.hide(this.refGrid);
          });
        }
        dialog.hide(this.refGrid);
      });
    }
  }


  /**
  * ページ情報変更イベント
  *
  * @param pageInfo ページ情報
  */
  onGoPageEvent(pageInfo: { page: number, perPage: number }) {
    // 初回呼出時に初期値を設定する
    if (this.currentPage < 0) {
      this.currentPage = this.initializePage = pageInfo.page;
      this.currentPerPage = pageInfo.perPage;
    }
    // 検索可否
    let changed = false;
    // 頁に関しての変化検知
    if (this.currentPage !== pageInfo.page) {
      changed = true;
      this.currentPage = pageInfo.page;
    }

    // 表示件数に関しての変化検知
    if (this.currentPerPage !== pageInfo.perPage) {
      changed = true;
      this.currentPerPage = pageInfo.perPage;
      this.currentPage = this.initializePage;
    }

    if (changed) {
      this.search(this.currentPage + 1, this.currentPerPage);
    }
  }


  /**
   * 検索条件をデフォルトに戻す
   */
  private resetCondition() {
    this.searchInfo.pageId = 'all'; // 全て表示

    const today = this.service.formatDate(DateTimeUtils.today());
    this.searchInfo.searchDateStart = today;
    this.searchInfo.searchDateEnd = today;
    this.searchInfo.outputStatusDiv = 'all';
  }

  /**
   * 日付を変換
   * @param date 日付
   */
  private convertDate(date: string) {
    let result = '';
    if (!_isNil(date) && date !== '') {
      result = DateTimeUtils.format(date, DateTimeOutput.YMD, DateTimePadding.ZERO, DateSeparator.NONE);
    }
    return result;
  }

  /**
   * 列幅調整を行います
   */
  adjustColumn() {
    _defer(() => {
      this.refGrid.adjustSizeColumnToFit();
    });
  }

  /**
   * ローディング処理
   *
   * @param param Loading表示オプション
   */
  private showLoading(param: LoadingOption = { message: '' }): void {
    this.blLoadingService.show(param);
  }

  /**
   * ローディング終了処理
   *
   * @param params Loading表示オプション
   */
  private hideLoading(focusTarget?: any): void {
    this.blLoadingService.hide(focusTarget);
  }

}



// WEBPACK FOOTER //
// ./src/app/page/report-output-status-page/report-output-status-page.component.ts